import { EmptyState } from "@/components/EmptyState";
import { Panel } from "@/components/Panel";
import { cn } from "@/lib/cn";
import { formatCurrency, formatNumber } from "@/lib/format";
import type { PaidSearchEfficiency, PaidSocialEfficiency } from "@/lib/types";
import { usePayload } from "@/lib/usePayload";
import { getPaidSocialEfficiency, getPaidSearchEfficiency } from "@/lib/viewModeSelectors";

export function ChannelEfficiency() {
  const { payload, isLoading, viewMode, monthLabel } = usePayload();
  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;
  
  const socialEfficiency = getPaidSocialEfficiency(payload, viewMode);
  const searchEfficiency = getPaidSearchEfficiency(payload, viewMode);
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Channel Efficiency</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Paid Social vs. Paid Search deep dive — {displayLabel}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChannelCard
          title="Paid Social"
          dotClass="bg-violet-500"
          spend={socialEfficiency.spend ?? socialEfficiency.meta_spend}
          mql={socialEfficiency.paid_social_mql}
          revenue={socialEfficiency.paid_social_revenue}
          roas={socialEfficiency.roas}
          social={socialEfficiency}
        />
        <ChannelCard
          title="Paid Search"
          dotClass="bg-blue-500"
          spend={searchEfficiency.google_spend}
          mql={searchEfficiency.paid_search_mql}
          revenue={searchEfficiency.paid_search_revenue}
          roas={searchEfficiency.roas}
          search={searchEfficiency}
        />
      </div>
    </div>
  );
}

interface ChannelCardProps {
  title: string;
  dotClass: string;
  spend?: number;
  mql?: number;
  revenue?: number;
  roas?: number | null;
  social?: PaidSocialEfficiency;
  search?: PaidSearchEfficiency;
}

function ChannelCard({ title, dotClass, spend, mql, revenue, roas, social, search }: ChannelCardProps) {
  const eff = social || search;
  const isSocial = !!social;
  const sql = isSocial ? social?.paid_social_sql : search?.paid_search_sql;
  const sqo = isSocial ? social?.paid_social_sqo : search?.paid_search_sqo;
  const wins = isSocial ? social?.paid_social_wins : search?.paid_search_wins;

  const kpis: { label: string; value: string }[] = [
    { label: "Cost per MQL", value: formatCurrency(eff?.cost_per_mql, { compact: true }) },
    { label: "Cost per SQL", value: formatCurrency(eff?.cost_per_sql, { compact: true }) },
    { label: "Cost per SQO", value: formatCurrency(eff?.cost_per_sqo, { compact: true }) },
    { label: "CAC (Cost per Win)", value: formatCurrency(eff?.cac, { compact: true }) },
  ];

  return (
    <Panel
      title={
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", dotClass)} />
          <span>{title}</span>
        </div>
      }
      right={
        <span className="text-xs text-slate-500 font-semibold numeric-value">
          {roas != null ? `${roas.toFixed(2)}× ROAS` : "—"}
        </span>
      }
    >
      <div className="grid grid-cols-3 gap-5">
        <BigStat label="Spend" value={formatCurrency(spend, { compact: true })} />
        <BigStat label="MQLs" value={formatNumber(mql)} />
        <BigStat label="Revenue" value={formatCurrency(revenue, { compact: true })} />
      </div>

      <div className="mt-5 pt-5 border-t border-border">
        <div className="label-xs mb-3">Efficiency KPIs</div>
        <div className="space-y-2.5">
          {kpis.map((k) => (
            <div key={k.label} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{k.label}</span>
              <span className="font-semibold numeric-value">
                {k.value}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between text-sm pt-1">
            <span className="text-slate-600">SQLs</span>
            <span className="font-semibold numeric-value">
              {formatNumber(sql)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">SQOs</span>
            <span className="font-semibold numeric-value">
              {formatNumber(sqo)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Wins</span>
            <span className="font-semibold numeric-value">
              {formatNumber(wins)}
            </span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label-xs">{label}</div>
      <div className="text-2xl mt-1.5 leading-none numeric-value">
        {value}
      </div>
    </div>
  );
}
